import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  LocalOffer,
  VisibilityOff,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import packageService from '../../services/packageService';
import { trackProviderSectionSave } from '../../services/analyticsService';
import QuillEditor from '../Common/QuillEditor';
import DialogHeader from '../Common/DialogHeader';
import { formatPackagePrice } from '../../utils/currency';

const QUILL_MODULES = {
  toolbar: [
    [{ size: ['small', false, 'large'] }],
    ['bold', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

const PRICE_UNIT_OPTIONS = [
  { value: 'total', label: 'Összesen (egység nélkül)' },
  { value: '/fő', label: '/fő (személyenként)' },
  { value: '/db', label: '/db (darabonként)' },
  { value: '/alkalom', label: '/alkalom' },
  { value: '/ora', label: '/óra' },
  { value: 'custom', label: 'Egyéni egység…' },
];

const EMPTY_FORM = {
  name: '',
  price: '',
  priceUnit: 'total',
  customUnit: '',
  shortDescription: '',
  fullDescription: '',
  isActive: true,
};

const formatPrice = formatPackagePrice;

const PackageEditor = ({ providerId }) => {
  const theme = useTheme();
  // Telefonon a csomag-dialógus teljes képernyős (sok mező + szövegszerkesztő)
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Azonnali validáció: a mező hibája az első elhagyása után (vagy mentési kísérletkor) látszik
  const [touched, setTouched] = useState({});
  const [initialForm, setInitialForm] = useState(EMPTY_FORM);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadPackages = useCallback(async () => {
    try {
      setLoading(true);
      const res = await packageService.getAllPackagesByProvider(providerId);
      if (res.success) setPackages(res.data.packages);
    } catch {
      showSnackbar('Nem sikerült betölteni a csomagokat', 'error');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const openCreateDialog = () => {
    setEditingPackage(null);
    setForm(EMPTY_FORM);
    setInitialForm(EMPTY_FORM);
    setTouched({});
    setDialogOpen(true);
  };

  const openEditDialog = (pkg) => {
    setEditingPackage(pkg);
    const isKnownUnit = PRICE_UNIT_OPTIONS.some(o => o.value === pkg.priceUnit && o.value !== 'custom');
    const loaded = {
      name: pkg.name,
      price: String(pkg.price),
      priceUnit: isKnownUnit ? pkg.priceUnit : 'custom',
      customUnit: isKnownUnit ? '' : pkg.priceUnit,
      shortDescription: pkg.shortDescription || '',
      fullDescription: pkg.fullDescription || '',
      isActive: pkg.isActive,
    };
    setForm(loaded);
    setInitialForm(loaded);
    setTouched({});
    setDialogOpen(true);
  };

  const formErrors = {};
  if (!form.name.trim()) formErrors.name = 'A csomag neve kötelező.';
  if (form.price === '') formErrors.price = 'Az ár kötelező.';
  else if (!/^\d+$/.test(String(form.price))) formErrors.price = 'Pozitív egész számot adjon meg.';
  if (form.priceUnit === 'custom' && !form.customUnit.trim()) formErrors.customUnit = 'Adja meg az egységet, pl. /asztal.';
  const showError = (field) => (touched[field] ? formErrors[field] : undefined);
  const touch = (field) => () => setTouched(prev => ({ ...prev, [field]: true }));
  const isFormDirty = JSON.stringify(form) !== JSON.stringify(initialForm);

  // A háttérre kattintás nem zárja be (a beírt tartalom elveszne); Esc csak módosítás nélkül
  const handleDialogClose = (event, reason) => {
    if (saving) return;
    if (reason === 'backdropClick') return;
    if (reason === 'escapeKeyDown' && isFormDirty) return;
    setDialogOpen(false);
  };

  const handleFormChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const getEffectivePriceUnit = () => {
    if (form.priceUnit === 'custom') return form.customUnit.trim() || 'total';
    return form.priceUnit;
  };

  const handleSave = async () => {
    if (Object.keys(formErrors).length > 0) {
      setTouched({ name: true, price: true, customUnit: true });
      return;
    }

    const payload = {
      name: form.name.trim(),
      price: parseInt(form.price, 10),
      priceUnit: getEffectivePriceUnit(),
      shortDescription: form.shortDescription.trim() || null,
      fullDescription: form.fullDescription.replace(/<[^>]*>/g, '').trim() ? form.fullDescription : null,
      isActive: form.isActive,
    };

    try {
      setSaving(true);
      if (editingPackage) {
        await packageService.updatePackage(editingPackage.id, payload);
        showSnackbar('Csomag frissítve');
      } else {
        await packageService.createPackage(providerId, payload);
        showSnackbar('Csomag létrehozva');
      }
      trackProviderSectionSave(providerId, 'csomagok');
      setDialogOpen(false);
      await loadPackages();
    } catch {
      showSnackbar('Mentés sikertelen', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (packageId) => {
    setDeleting(true);
    try {
      await packageService.deletePackage(packageId);
      showSnackbar('Csomag törölve');
      setDeleteConfirmId(null);
      await loadPackages();
    } catch {
      showSnackbar('Törlés sikertelen', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {/* Fejléc */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.dark }}>
            Szolgáltatási csomagok
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            A csomagok megjelennek a profilján, és az ajánlatkérő űrlapon választhatók lesznek.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreateDialog}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, flexShrink: 0, minHeight: 40 }}
        >
          Új csomag
        </Button>
      </Box>

      {/* Árak szinkronizálás info */}
      {packages.some(p => p.isActive) && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
          A profilján megjelenő árak automatikusan a csomagjai ára alapján frissülnek.
        </Alert>
      )}

      {/* Csomag lista */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : packages.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: '16px',
            borderStyle: 'dashed',
            borderColor: theme.palette.divider,
          }}
        >
          <LocalOffer sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="body1" color="text.secondary">
            Még nincsenek csomagok. Hozzon létre egyet!
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {packages.map((pkg) => (
            <Paper
              key={pkg.id}
              variant="outlined"
              sx={{
                p: { xs: 1.5, sm: 2.5 },
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                opacity: pkg.isActive ? 1 : 0.6,
                borderColor: pkg.isActive ? theme.palette.divider : theme.palette.action.disabledBackground,
              }}
            >
              {/* Csomag infó */}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                    {pkg.name}
                  </Typography>
                  {!pkg.isActive && (
                    <Tooltip title="Nem aktív – nem látható a profilon">
                      <Chip
                        icon={<VisibilityOff sx={{ fontSize: '0.9rem !important' }} />}
                        label="Inaktív"
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.72rem' }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                  {formatPrice(pkg.price, pkg.priceUnit)}
                </Typography>
                {pkg.shortDescription && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      mt: 0.25,
                    }}
                  >
                    {pkg.shortDescription}
                  </Typography>
                )}
              </Box>

              {/* Műveletek */}
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <Tooltip title="Szerkesztés">
                  <IconButton size="small" aria-label="Szerkesztés" onClick={() => openEditDialog(pkg)} sx={{ width: 40, height: 40 }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Törlés">
                  <IconButton size="small" aria-label="Törlés" color="error" onClick={() => setDeleteConfirmId(pkg.id)} sx={{ width: 40, height: 40 }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Csomag szerkesztő dialóg */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
        disableEnforceFocus
        PaperProps={isPhone ? { sx: { borderRadius: 0 } } : undefined}
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<LocalOffer />}
            title={editingPackage ? 'Csomag szerkesztése' : 'Új csomag létrehozása'}
            onClose={() => !saving && setDialogOpen(false)}
          />
        </DialogTitle>

        <DialogContent sx={{ pt: '20px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Név */}
            <TextField
              label="Csomagnév"
              required
              value={form.name}
              onChange={handleFormChange('name')}
              onBlur={touch('name')}
              fullWidth
              inputProps={{ maxLength: 150 }}
              error={!!showError('name')}
              helperText={showError('name') || `${form.name.length}/150`}
            />

            {/* Ár + egység */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                label="Ár (Ft)"
                required
                value={form.price}
                onChange={handleFormChange('price')}
                onBlur={touch('price')}
                type="number"
                inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
                error={!!showError('price')}
                helperText={showError('price')}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ flex: 1.4 }}>
                <InputLabel>Egység</InputLabel>
                <Select
                  value={form.priceUnit}
                  onChange={handleFormChange('priceUnit')}
                  label="Egység"
                >
                  {PRICE_UNIT_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Egyéni egység */}
            {form.priceUnit === 'custom' && (
              <TextField
                label="Egyéni egység (pl. /asztal)"
                required
                value={form.customUnit}
                onChange={handleFormChange('customUnit')}
                onBlur={touch('customUnit')}
                error={!!showError('customUnit')}
                helperText={showError('customUnit')}
                fullWidth
                inputProps={{ maxLength: 30 }}
                placeholder="/egység"
              />
            )}

            <Divider />

            {/* Rövid leírás */}
            <TextField
              label="Rövid leírás"
              value={form.shortDescription}
              onChange={handleFormChange('shortDescription')}
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: 120 }}
              helperText={`${form.shortDescription.length}/120 – Ez jelenik meg a csomag kártyán (max 2 sor)`}
            />

            {/* Teljes leírás */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                Részletes leírás – kattintásra megjelenő tartalom (mi van benne, feltételek, stb.)
              </Typography>
              <Box sx={{
                '& .ql-container': { minHeight: 160, fontSize: 14 },
                '& .ql-editor': { minHeight: 160 },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}>
                <QuillEditor
                  value={form.fullDescription}
                  onChange={(html) => setForm(prev => ({ ...prev, fullDescription: html }))}
                  modules={QUILL_MODULES}
                  placeholder="Írja le a csomag részleteit..."
                />
              </Box>
            </Box>

            {/* Aktív kapcsoló */}
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  color="primary"
                />
              }
              label={form.isActive ? 'Aktív (látható a profilon)' : 'Inaktív (nem jelenik meg)'}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={saving}
          >
            Mégse
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ minWidth: 100 }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Mentés'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Törlés megerősítő dialóg */}
      <Dialog
        open={Boolean(deleteConfirmId)}
        onClose={() => !deleting && setDeleteConfirmId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<Delete />} title="Csomag törlése" />
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Typography>Biztosan törölni szeretné ezt a csomagot? Ez a művelet nem visszavonható.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setDeleteConfirmId(null)} disabled={deleting}>
            Mégse
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleDelete(deleteConfirmId)}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            Törlés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        // A profilszerkesztő minden fülén ugyanott: alul középen, a mentési sáv és a lebegő gomb fölött
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 96, sm: 104 } }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ borderRadius: '12px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PackageEditor;
